import { Link } from "react-router-dom";
import PhotoGallery from "@/components/base/PhotoGallery";
import { reviewPhotoAlt } from "@/lib/imageAlt";
import type { Testimonial } from "@/mocks/testimonials";

export default function ReviewCard({ review }: { review: Testimonial }) {
  const photos = review.photos.map((src, index) => ({
    src,
    alt: reviewPhotoAlt(
      review.photoAlts,
      index,
      `${review.name} on the ${review.experience || "farm"} experience in ${review.village || "Konaseema"}`,
    ),
  }));

  return (
    <article className="h-full bg-background-100 rounded-lg border border-background-200 p-6 flex flex-col">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-1 text-accent-500">
          {Array.from({ length: review.rating }).map((_, i) => (
            <i key={i} className="ri-star-fill text-sm"></i>
          ))}
        </div>
        <span className="text-xs text-foreground-500 whitespace-nowrap">{review.date}</span>
      </div>

      <blockquote className="text-sm md:text-base text-foreground-700 leading-relaxed mt-4">
        "{review.quote}"
      </blockquote>

      <div className="mt-6">
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-3">
          <i className="ri-image-2-line text-accent-500"></i>
          From their visit
        </span>
        <PhotoGallery images={photos} layout="strip" />
      </div>

      <div className="mt-6 pt-5 border-t border-background-200 flex items-center gap-3">
        <span className="w-12 h-12 rounded-full overflow-hidden bg-background-200 shrink-0">
          <img
            src={review.avatar}
            width={200}
            height={200}
            alt={`Portrait of ${review.name}, a Rythugaru visitor from ${review.location}`}
            title={`${review.name} - Rythugaru visitor review`}
            className="w-full h-full object-cover object-top"
            decoding="async"
          />
        </span>
        <span className="min-w-0">
          <span className="block font-semibold text-foreground-950">{review.name}</span>
          <span className="block text-xs text-foreground-600 mt-0.5 truncate">
            {review.role} · {review.location} · {review.village}
          </span>
        </span>
      </div>

      <Link
        to={`/experiences/${review.experienceSlug}`}
        className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
      >
        <i className="ri-plant-line"></i>
        {review.experience}
        <i className="ri-arrow-right-line"></i>
      </Link>
    </article>
  );
}